import { ReactNode, useEffect, useState } from "react";
import { IngestFileService } from "@services/ingest/ingestFileService.ts";
import type { BilledPeriods } from "@models/population.model";
import type { SegmentLinkLaunch } from "@services/engagements/commands/cmdGetSegmentLinks";
import BilledPeriodOverrideDialog from "./BilledPeriodOverrideDialog";

const fileService = new IngestFileService();

interface Options {
  /** Any data spec feeding the population: the rebuild covers every spec feeding it. */
  definitionId: number | null | undefined;
  populationName?: string;
  /** This population's campaigns, for the no-go-live-date warning (MPN-1827). */
  launches?: SegmentLinkLaunch[];
  onStarted: (jobId: number, message: string) => void;
  onError: (message: string) => void;
}

/*
    "Reprocess all files" for a population: the pre-flight, the confirm, the billed-period
    override, and the start. One hook so the population header (MPN-1945) and the
    standalone Files page say exactly the same thing before the click; each host lays
    the button and its note out itself.
*/
export function useReprocessAll({ definitionId, populationName, launches, onStarted, onError }: Options) {
  // Named in the button and the confirm so nobody reads "reprocess" as one data
  // spec's files alone: the job rebuilds the population across every spec feeding it.
  const populationLabel = populationName ? `"${populationName}"` : "this population";

  // Billed-period override (MPN-1516): checked before the reprocess runs, so the
  // operator sees which invoiced months are at risk instead of learning it from
  // a job that fails a second after the click.
  const [billedPrompt, setBilledPrompt] = useState<BilledPeriods | null>(null);
  // Whether an audience on this population is live on a network: the server's own
  // answer, from the same pre-flight the override dialog reads. Null until it arrives.
  const [published, setPublished] = useState<boolean | null>(null);
  const [starting, setStarting] = useState(false);

  // Publish state decides which rebuild runs, and the page says so BEFORE the click
  // (MPN-1817), so ask on mount, not on click.
  useEffect(() => {
    if (!definitionId) return;
    fileService
      .getDefinitionBilledPeriods(definitionId)
      .then((b) => setPublished(b.published ?? null))
      .catch(() => setPublished(null));
  }, [definitionId]);

  // The server picks the rebuild flavour from publish state, so the page can only
  // repeat its answer: one sentence, used on the page, in the confirm and in the
  // tooltip (MPN-1817). Until the pre-flight answers, state the rule instead.
  const rebuildScope =
    published === true
      ? `Because ${populationLabel} is published, conversions are rebuilt and audience members, ` +
        "holdouts and network links are kept."
      : published === false
        ? `Nothing in ${populationLabel} is published yet, so audience members and conversions ` +
          "are both rebuilt."
        : "Conversions are rebuilt from scratch. If nothing is published yet, audience members " +
          "are rebuilt too.";

  // A campaign with no go-live date has no billing boundary: every conversion in its
  // files loads as a pre-convert and nothing bills (MPN-1687). Right while a campaign
  // is being staged; wrong for a migrated one whose whole history is about to be
  // replayed. Northbridge rebuilt 140 files to zero conversions this way (MPN-1827).
  // A warning, not a refusal: the staging load and the MPN-1752 reset both replay
  // files with no date on purpose.
  const undatedLaunches = (launches ?? []).filter((l) => !l.go_live_date);
  const undatedWarning = undatedLaunches.length
    ? `${undatedLaunches.map((l) => l.name).join(", ")} ${undatedLaunches.length === 1 ? "has" : "have"} ` +
      "no go-live date, so every conversion in these files will load as a pre-convert and " +
      "nothing will bill. For a migrated campaign, set the go-live date first " +
      "(set_launch_go_live_date), then reprocess."
    : null;

  const begin = async (override?: { reason: string }) => {
    if (!definitionId) return;
    setStarting(true);
    try {
      const { job_id, replay_through, mode } = await fileService.reprocessAllFiles(definitionId, override);
      onStarted(
        job_id,
        mode === "conversions_only"
          ? `Rebuilding conversions${replay_through ? ` through ${replay_through}` : ""}. Audience members and holdouts are kept (already published).`
          : `Rebuilding everything${replay_through ? ` through ${replay_through}` : ""}…`,
      );
    } catch (err) {
      onError(err instanceof Error ? err.message : "Reprocess failed to start");
    } finally {
      setStarting(false);
    }
  };

  const start = async () => {
    if (!definitionId) return;
    // Billed months get the dedicated dialog instead of the plain confirm: the
    // operator needs to see WHICH months and give a reason (MPN-1516). A failed
    // pre-flight falls through to the normal path: the job's own guard is what
    // actually refuses, so an older server or a blip cannot let a rewrite through.
    try {
      const billed = await fileService.getDefinitionBilledPeriods(definitionId);
      if (billed.requires_override) {
        setBilledPrompt(billed);
        return;
      }
    } catch {
      /* fall through to the unbilled confirm */
    }
    if (
      !window.confirm(
        `Re-run every stored file for ${populationLabel}, oldest first?\n\n` +
          "This rebuilds the whole population: files from every data spec that feeds " +
          "it are included, and so are files that were never processed. " +
          "Unvalidated ones are checked first. A file that fails blocks the rebuild before " +
          "anything is deleted, until it is fixed or discarded.\n\n" +
          `${rebuildScope} The files themselves are kept.` +
          (undatedWarning ? `\n\n${undatedWarning}` : ""),
      )
    ) {
      return;
    }
    await begin();
  };

  const dialog: ReactNode = (
    <BilledPeriodOverrideDialog
      open={billedPrompt !== null}
      billed={billedPrompt}
      onCancel={() => setBilledPrompt(null)}
      onConfirm={(reason) => {
        setBilledPrompt(null);
        void begin({ reason });
      }}
    />
  );

  return { start, starting, rebuildScope, undatedWarning, populationLabel, dialog };
}
